import { Camera, Gift, Heart, Megaphone, Repeat, Sparkles, Users } from "lucide-react";
import { MISSIONS, VALUES } from "@/lib/ambassadors";
import { A, Eyebrow, Reveal, btnDark, btnLight } from "./ui";

const VALUE_ICONS = [Heart, Repeat, Users];
const MISSION_ICONS = { posts: Camera, stories: Sparkles, brand: Megaphone };

export function AmbHero() {
  return (
    <section data-testid="amb-hero" className={`${A.bg} relative overflow-hidden px-5 pb-20 pt-32 md:px-10 md:pt-40`}>
      <div className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-[1.1fr_1fr]">
        <div>
          <Eyebrow>Programme Ambassadeurs</Eyebrow>
          <h1 data-testid="amb-hero-title" className={`font-display text-4xl leading-[1.05] sm:text-5xl lg:text-6xl ${A.ink}`}>Deviens Ambassadeur <span className="italic text-[#7A5418]">Ozentya</span> et promeus le cheveu afro</h1>
          <p className={`mt-6 max-w-xl text-base leading-relaxed md:text-lg ${A.muted}`}>Représente une marque que tu aimes, partage ton univers sur les réseaux sociaux et profite d'avantages exclusifs chaque mois.</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#programme" data-testid="amb-hero-discover" className={btnLight}>Découvrir le programme</a>
            <a href="#candidature" data-testid="amb-hero-join" className={btnDark}>Rejoindre la communauté</a>
          </div>
          <div className="mt-8 flex flex-wrap gap-3 text-sm">
            <span data-testid="amb-hero-perk-products" className={`flex items-center gap-2 rounded-full border ${A.border} ${A.ivory} px-4 py-2 ${A.ink}`}><Gift className="h-4 w-4 text-[#7A5418]" /> 2 produits offerts / mois</span>
            <span data-testid="amb-hero-perk-code" className={`flex items-center gap-2 rounded-full border ${A.border} ${A.ivory} px-4 py-2 ${A.ink}`}><span className="font-semibold text-[#7A5418]">-20 %</span> code personnel</span>
          </div>
          <p className={`mt-6 text-sm italic ${A.muted}`}>Une collaboration basée sur le partage, l'authenticité et la régularité.</p>
        </div>
        <div className="relative">
          <div className="absolute -inset-4 rounded-[28px] bg-[#E7D3C0]" aria-hidden="true" />
          <img src="/editorial/coffret-vrais-produits-ozentya.webp" alt="Produits de soin Ozentya" fetchPriority="high"
            className="relative aspect-[4/5] w-full rounded-[22px] object-cover shadow-xl" />
        </div>
      </div>
    </section>
  );
}

export function AmbConcept() {
  return (
    <section id="programme" data-testid="amb-concept" className={`${A.ivory} scroll-mt-24 px-5 py-20 md:px-10 md:py-28`}>
      <div className="mx-auto max-w-6xl">
        <Reveal className="max-w-3xl">
          <Eyebrow>Le concept</Eyebrow>
          <h2 className={`font-display text-3xl md:text-4xl ${A.ink}`}>Une collaboration entre Ozentya et toi</h2>
          <p className={`mt-6 text-base leading-relaxed md:text-lg ${A.muted}`}>En rejoignant le programme Ambassadeurs Ozentya, tu participes au développement de la marque en partageant nos produits, nos nouveautés et nos offres auprès de ta communauté. En échange de ton engagement régulier, Ozentya te réserve des avantages exclusifs chaque mois.</p>
        </Reveal>
        <div className="mt-12 grid gap-5 md:grid-cols-3">
          {VALUES.map((v, i) => {
            const Icon = VALUE_ICONS[i];
            return (
              <Reveal key={v.title} delay={i * 0.08}>
                <div data-testid={`amb-value-${i}`} className={`h-full rounded-2xl border ${A.border} bg-white p-7 transition-transform duration-300 hover:-translate-y-1`}>
                  <Icon className="h-6 w-6 text-[#7A5418]" />
                  <h3 className={`mt-5 font-display text-2xl ${A.ink}`}>{v.title}</h3>
                  <p className={`mt-3 text-sm leading-relaxed ${A.muted}`}>{v.text}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function AmbMissions() {
  return (
    <section id="missions" data-testid="amb-missions" className={`${A.bg} scroll-mt-24 px-5 py-20 md:px-10 md:py-28`}>
      <div className="mx-auto max-w-6xl">
        <Reveal><Eyebrow>Chaque mois</Eyebrow><h2 className={`font-display text-3xl md:text-4xl ${A.ink}`}>Tes missions chaque mois</h2>
          <p data-testid="amb-missions-intro" className={`mt-5 max-w-3xl rounded-2xl bg-[#141013] p-5 text-base leading-relaxed text-[#FBF8F3]`}>
            <b className="text-[#E5C88F]">Ta mission principale : créer du contenu.</b> Chaque mois, tu crées toi-même des contenus (photos, vidéos, Reels, TikTok) avec les produits Ozentya, puis tu les publies en <b className="text-[#E5C88F]">posts</b> et en <b className="text-[#E5C88F]">stories</b> sur tes réseaux sociaux.
          </p></Reveal>
        <div className="mt-12 grid gap-5 md:grid-cols-3">
          {MISSIONS.map((m, i) => {
            const Icon = MISSION_ICONS[m.key];
            return (
              <Reveal key={m.key} delay={i * 0.08}>
                <div data-testid={`amb-mission-${m.key}`} className={`h-full rounded-2xl border ${A.border} ${A.ivory} p-7`}>
                  <div className="flex items-center justify-between"><span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#141013] text-[#F1DBA6]"><Icon className="h-5 w-5" /></span>
                    <span className="font-display text-4xl text-[#D9C2A3]">0{i + 1}</span></div>
                  <h3 className={`mt-6 font-display text-2xl ${A.ink}`}>{m.title}</h3>
                  <p className={`mt-3 text-sm leading-relaxed ${A.muted}`}>{m.text}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
        <p data-testid="amb-missions-rule" className={`mt-8 max-w-3xl rounded-2xl border ${A.border} bg-white p-5 text-sm leading-relaxed ${A.ink}`}>
          Les ambassadeurs s'engagent à respecter les consignes de communication de la marque et à publier des contenus personnels et sincères.
        </p>
      </div>
    </section>
  );
}
